import jsPDF from 'jspdf';
import logo from 'utils/assets/img/logo.png';
import { PrestazioneData } from 'utils/interfaces';
import { PRESTAZIONE_GROUPS } from '../prestazioneConfig';
import { displayableClientEmail, ReportFormState, SERVICE_RATING_OPTIONS } from '../types';

interface GenerateReportPdfParams {
  reportNumberFormatted: string;
  reportDate: string;
  taskTitle: string;
  compiledByLabel: string;
  employeeSignatureUrl: string | null;
  data: ReportFormState;
}

// html2canvas (DOM screenshot -> canvas -> PDF) was dropped: it kept producing
// pages with borders/checkboxes/images but no text (or, with foreignObjectRendering,
// a fully blank page) across several rounds of fixes — a known class of bug with
// its hand-rolled text engine (CSS grid, inherited color, text-transform, and
// blob: URLs inside its cloned render context). Drawing straight into jsPDF with
// its own vector text/shape API sidesteps all of that: no screenshotting, no
// webfont loading race (jsPDF's 'helvetica' is a standard PDF font, always
// available), and the two signatures are embedded as real images via addImage.
interface LoadedImage {
  width: number;
  height: number;
  // Signatures can be uploaded as PNG or JPG (see the employee signature
  // feature), but addImage() needs to be told the real encoded format or it
  // silently produces garbage. Redrawing onto a canvas and re-exporting
  // normalizes every source (PNG, JPG, data:, blob:) to a guaranteed PNG.
  pngDataUrl: string;
}

const loadImageAsPng = (src: string): Promise<LoadedImage | null> =>
  new Promise(resolve => {
    const img = new Image();
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth || img.width;
        canvas.height = img.naturalHeight || img.height;
        const ctx = canvas.getContext('2d');
        if (!ctx || canvas.width === 0 || canvas.height === 0) {
          resolve(null);
          return;
        }
        ctx.drawImage(img, 0, 0);
        resolve({ width: canvas.width, height: canvas.height, pngDataUrl: canvas.toDataURL('image/png') });
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });

const COLOR_TEXT = '#1a2942';
const COLOR_MUTED = '#64748b';
const COLOR_BORDER = '#94a3b8';
const COLOR_BORDER_LIGHT = '#cbd5e1';
const COLOR_HEADER_BG = '#eef2f7';
const COLOR_BAND_1 = '#37492e';
const COLOR_BAND_2 = '#9ace38';
const COLOR_BAND_3 = '#5ea9f3';

export const generateReportPdf = async (params: GenerateReportPdfParams): Promise<Blob> => {
  const { reportNumberFormatted, reportDate, taskTitle, compiledByLabel, employeeSignatureUrl, data } = params;

  const [logoImg, employeeSigImg, customerSigImg] = await Promise.all([
    loadImageAsPng(logo),
    employeeSignatureUrl ? loadImageAsPng(employeeSignatureUrl) : Promise.resolve(null),
    data.customerSignatureDataUrl ? loadImageAsPng(data.customerSignatureDataUrl) : Promise.resolve(null),
  ]);

  const pdf = new jsPDF({ unit: 'pt', format: 'a4' });
  const PAGE_WIDTH = pdf.internal.pageSize.getWidth();
  const PAGE_HEIGHT = pdf.internal.pageSize.getHeight();
  const MARGIN = 28;
  const CONTENT_WIDTH = PAGE_WIDTH - MARGIN * 2;

  pdf.setFont('helvetica', 'normal');

  let y = MARGIN;

  const drawPageBorder = () => {
    pdf.setDrawColor(COLOR_BORDER);
    pdf.setLineWidth(0.75);
    pdf.rect(MARGIN, MARGIN, CONTENT_WIDTH, PAGE_HEIGHT - MARGIN * 2, 'S');
  };

  const ensureSpace = (height: number) => {
    if (y + height > PAGE_HEIGHT - MARGIN) {
      pdf.addPage();
      y = MARGIN;
      drawPageBorder();
    }
  };

  drawPageBorder();

  const fitImage = (img: LoadedImage, maxW: number, maxH: number) => {
    const ratio = Math.min(maxW / img.width, maxH / img.height);
    return { w: img.width * ratio, h: img.height * ratio };
  };

  const sectionHeaderAt = (label: string, x: number, width: number, topY: number, h = 14) => {
    pdf.setFillColor(COLOR_HEADER_BG);
    pdf.rect(x, topY, width, h, 'F');
    pdf.setDrawColor(COLOR_BORDER);
    pdf.setLineWidth(0.5);
    pdf.rect(x, topY, width, h, 'S');
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(8);
    pdf.setTextColor(COLOR_TEXT);
    pdf.text(label.toUpperCase(), x + 5, topY + h / 2 + 2.5);
    return h;
  };

  const sectionHeader = (label: string) => {
    ensureSpace(14);
    const h = sectionHeaderAt(label, MARGIN, CONTENT_WIDTH, y);
    y += h;
  };

  const drawField = (label: string, value: string, x: number, width: number, topY: number) => {
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(6.5);
    pdf.setTextColor(COLOR_MUTED);
    pdf.text(label.toUpperCase(), x, topY + 7);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);
    pdf.setTextColor(COLOR_TEXT);
    pdf.text(value || '', x, topY + 17, { maxWidth: width });
    pdf.setDrawColor(COLOR_BORDER);
    pdf.setLineWidth(0.5);
    pdf.line(x, topY + 19, x + width, topY + 19);
  };

  const drawFieldRow = (
    fields: { label: string; value: string; widthRatio: number }[],
    topY: number,
    gap = 8,
    startX = MARGIN + 4,
    totalWidth = CONTENT_WIDTH - 8
  ) => {
    const totalRatio = fields.reduce((s, f) => s + f.widthRatio, 0);
    let x = startX;
    const usableWidth = totalWidth - gap * (fields.length - 1);
    fields.forEach(f => {
      const w = usableWidth * (f.widthRatio / totalRatio);
      drawField(f.label, f.value, x, w, topY);
      x += w + gap;
    });
  };

  const drawCheckbox = (x: number, topY: number, checked: boolean, size = 7) => {
    pdf.setDrawColor(COLOR_BORDER);
    pdf.setLineWidth(0.6);
    pdf.rect(x, topY, size, size, 'S');
    if (checked) {
      pdf.setFillColor(COLOR_TEXT);
      pdf.rect(x + 1.3, topY + 1.3, size - 2.6, size - 2.6, 'F');
    }
  };

  // ── Header ──────────────────────────────────────────────────────────────
  ensureSpace(24);
  if (logoImg) {
    const { w, h } = fitImage(logoImg, 90, 16);
    pdf.addImage(logoImg.pngDataUrl, 'PNG', MARGIN + (CONTENT_WIDTH - w) / 2, y + 2, w, h);
  }
  y += 22;

  const bandH = 16;
  const bandW = CONTENT_WIDTH / 3;
  const bands: { label: string; bg: string; color: string }[] = [
    { label: 'Recupero Rifiuti', bg: COLOR_BAND_1, color: '#ffffff' },
    { label: 'Noleggio WC e Cassoni', bg: COLOR_BAND_2, color: COLOR_TEXT },
    { label: 'Autospurgo', bg: COLOR_BAND_3, color: '#ffffff' },
  ];
  bands.forEach((band, idx) => {
    const x = MARGIN + bandW * idx;
    pdf.setFillColor(band.bg);
    pdf.rect(x, y, bandW, bandH, 'F');
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(8);
    pdf.setTextColor(band.color);
    pdf.text(band.label.toUpperCase(), x + bandW / 2, y + bandH / 2 + 2.5, { align: 'center' });
  });
  y += bandH;

  pdf.setDrawColor(COLOR_BORDER);
  pdf.setLineWidth(0.5);
  pdf.line(MARGIN, y, MARGIN + CONTENT_WIDTH, y);
  y += 4;
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(9);
  pdf.setTextColor(COLOR_TEXT);
  pdf.text(`FOGLIO DI LAVORO N. ${reportNumberFormatted}`, MARGIN + 4, y + 8);
  pdf.text(`DATA ${reportDate}`, MARGIN + CONTENT_WIDTH - 4, y + 8, { align: 'right' });
  y += 14;
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(7.5);
  pdf.setTextColor(COLOR_MUTED);
  pdf.text(`Intervento: ${taskTitle}`, MARGIN + 4, y + 6);
  y += 10;
  pdf.setDrawColor(COLOR_BORDER);
  pdf.line(MARGIN, y, MARGIN + CONTENT_WIDTH, y);
  y += 2;

  // ── Cliente ─────────────────────────────────────────────────────────────
  sectionHeader('Cliente');
  y += 4;
  drawFieldRow([{ label: 'Cliente', value: data.clientName, widthRatio: 1 }], y);
  y += 20;
  drawFieldRow(
    [
      { label: 'Comune', value: data.clientCity, widthRatio: 45 },
      { label: 'Indirizzo', value: data.clientAddress, widthRatio: 55 },
    ],
    y
  );
  y += 20;
  drawFieldRow(
    [
      { label: 'C.F. / P.IVA', value: data.clientTaxCode || data.clientVatNumber, widthRatio: 1 },
      { label: 'Tel.', value: data.clientPhone, widthRatio: 1 },
      { label: 'Mail', value: displayableClientEmail(data.clientEmail), widthRatio: 1 },
    ],
    y
  );
  y += 22;

  // ── Prestazione ─────────────────────────────────────────────────────────
  sectionHeader('Prestazione');
  const value = data.prestazione;

  // Draws one checkbox + label (+ optional underlined textKey value) cell, mirroring the
  // primary/child/flatList/alignEnd layout of PrestazioneSection.tsx.
  const drawPrestazioneCell = (
    item: (typeof PRESTAZIONE_GROUPS)[number]['items'][number],
    x: number,
    cellY: number,
    width: number,
    bold: boolean
  ) => {
    let textX = x;
    if (!item.noCheckbox) {
      drawCheckbox(x, cellY, !!value[item.key], 6);
      textX = x + 9;
    }
    pdf.setFont('helvetica', bold ? 'bold' : 'normal');
    pdf.setFontSize(bold ? 7.5 : 7);
    pdf.setTextColor(COLOR_TEXT);
    const label = item.label.toUpperCase();
    pdf.text(label, textX, cellY + 5.5, { maxWidth: Math.max(width - (textX - x), 4) });

    if (item.textKey) {
      const labelWidth = pdf.getTextWidth(label);
      const lineStartX = textX + labelWidth + 4;
      const lineEndX = x + width;
      if (lineEndX > lineStartX) {
        pdf.setDrawColor(COLOR_BORDER);
        pdf.setLineWidth(0.4);
        pdf.line(lineStartX, cellY + 6.5, lineEndX, cellY + 6.5);
        const textValue = (value[item.textKey as keyof PrestazioneData] as string | null) || '';
        if (textValue) {
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(6.5);
          pdf.text(textValue, lineStartX + 2, cellY + 5.5, { maxWidth: Math.max(lineEndX - lineStartX - 4, 4) });
        }
      }
    }
  };

  const drawGroupDivider = () => {
    pdf.setDrawColor(COLOR_BORDER_LIGHT);
    pdf.setLineWidth(0.4);
    pdf.line(MARGIN + 4, y, MARGIN + CONTENT_WIDTH - 4, y);
    y += 3;
  };

  PRESTAZIONE_GROUPS.forEach(group => {
    // Flat checklist groups (CER codes, Consegna/Pulizia WC/Ritiro): equal pari-livello
    // checkboxes wrapped in a compact 3-column grid, matching the paper form.
    if (group.flatList) {
      const cols = 3;
      const colWidth = CONTENT_WIDTH / cols;
      let col = 0;
      let rowY = y;
      group.items.forEach(item => {
        if (col === 0) {
          ensureSpace(13);
          rowY = y;
        }
        drawPrestazioneCell(item, MARGIN + colWidth * col + 4, rowY + 1, colWidth - 8, false);
        col += 1;
        if (col >= cols) {
          col = 0;
          y = rowY + 12;
        }
      });
      if (col !== 0) y = rowY + 12;
      drawGroupDivider();
      return;
    }

    // Every other group: first item is printed on its own row, at the same level as the rest
    // (no bold treatment); alignEnd siblings (e.g. "CER ___" / "FIR ___") print flush-right on
    // that same row, the remaining items are indented sub-options wrapped below.
    const [primary, ...rest] = group.items;
    const alignEndItems = rest.filter(i => i.alignEnd);
    const childItems = rest.filter(i => !i.alignEnd);

    ensureSpace(13);
    const alignEndWidth = 62;
    const primaryWidth = alignEndItems.length > 0
      ? CONTENT_WIDTH - 8 - alignEndWidth * alignEndItems.length
      : CONTENT_WIDTH - 8;
    drawPrestazioneCell(primary, MARGIN + 4, y + 1, primaryWidth, false);
    alignEndItems.forEach((item, idx) => {
      const x = MARGIN + CONTENT_WIDTH - 4 - alignEndWidth * (alignEndItems.length - idx);
      drawPrestazioneCell(item, x, y + 1, alignEndWidth - 4, false);
    });
    y += 12;

    if (childItems.length > 0) {
      const cols = 3;
      const colWidth = (CONTENT_WIDTH - 12) / cols;
      let col = 0;
      let rowY = y;
      childItems.forEach(item => {
        if (col === 0) {
          ensureSpace(11);
          rowY = y;
        }
        drawPrestazioneCell(item, MARGIN + 12 + colWidth * col, rowY, colWidth - 4, false);
        col += 1;
        if (col >= cols) {
          col = 0;
          y = rowY + 11;
        }
      });
      if (col !== 0) y = rowY + 11;
    }

    drawGroupDivider();
  });
  y += 4;

  // ── Note ────────────────────────────────────────────────────────────────
  sectionHeader('Note');
  y += 6;
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(8);
  pdf.setTextColor(COLOR_TEXT);
  const noteLines: string[] = data.notes ? pdf.splitTextToSize(data.notes, CONTENT_WIDTH - 8) : [];
  const noteHeight = Math.max(28, noteLines.length * 10 + 6);
  ensureSpace(noteHeight);
  if (noteLines.length > 0) {
    pdf.text(noteLines, MARGIN + 4, y + 6);
  }
  y += noteHeight;

  // ── Operatore / Presenti ───────────────────────────────────────────────
  const halfGap = 8;
  const halfWidth = (CONTENT_WIDTH - halfGap) / 2;
  const opX = MARGIN;
  const prX = MARGIN + halfWidth + halfGap;

  const rowH = 12;
  const opContentH = Math.max(1, data.operators.length) * rowH + 6;
  const prContentH = Math.max(1, data.attendees.length) * rowH + 6;
  const columnsBlockH = 14 + Math.max(opContentH, prContentH);
  ensureSpace(columnsBlockH);
  const opSectionTop = y;

  sectionHeaderAt('Operatore', opX, halfWidth, opSectionTop);
  sectionHeaderAt('Presenti', prX, halfWidth, opSectionTop);

  let opRowY = opSectionTop + 14 + 4;
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(7.5);
  if (data.operators.length === 0) {
    pdf.setTextColor(COLOR_MUTED);
    pdf.text('Nessun operatore', opX + 4, opRowY + 6);
  } else {
    data.operators.forEach(op => {
      drawCheckbox(opX + 4, opRowY, op.present, 6);
      pdf.setTextColor(COLOR_TEXT);
      pdf.text(`${op.firstName} ${op.lastName}`, opX + 14, opRowY + 6, { maxWidth: halfWidth - 18 });
      opRowY += rowH;
    });
  }

  let prRowY = opSectionTop + 14 + 4;
  if (data.attendees.length === 0) {
    pdf.setTextColor(COLOR_MUTED);
    pdf.text('Nessun presente', prX + 4, prRowY + 6);
  } else {
    data.attendees.forEach(a => {
      drawCheckbox(prX + 4, prRowY, a.present, 6);
      pdf.setTextColor(COLOR_TEXT);
      const label = a.note ? `${a.fullName} (${a.note})` : a.fullName;
      pdf.text(label, prX + 14, prRowY + 6, { maxWidth: halfWidth - 18 });
      prRowY += rowH;
    });
  }

  y = opSectionTop + columnsBlockH;

  // ── Ora inizio/fine + Mezzi ─────────────────────────────────────────────
  const vehicleColsPerRow = 2;
  const vehicleColWidth = (halfWidth - 8) / vehicleColsPerRow;
  const vehicleRows = Math.max(1, Math.ceil(data.vehicles.length / vehicleColsPerRow));
  const orarioBlockH = 14 + Math.max(24, vehicleRows * rowH + 8);
  ensureSpace(orarioBlockH);
  const orarioTop = y;

  sectionHeaderAt('Ora inizio / fine lavoro', opX, halfWidth, orarioTop);
  sectionHeaderAt('Mezzi utilizzati', prX, halfWidth, orarioTop);

  drawFieldRow(
    [
      { label: 'Inizio', value: data.startTime, widthRatio: 1 },
      { label: 'Fine', value: data.endTime, widthRatio: 1 },
    ],
    orarioTop + 14 + 4,
    8,
    opX + 4,
    halfWidth - 8
  );

  if (data.vehicles.length === 0) {
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(7.5);
    pdf.setTextColor(COLOR_MUTED);
    pdf.text('Nessun mezzo', prX + 4, orarioTop + 14 + 10);
  } else {
    data.vehicles.forEach((v, idx) => {
      const vx = prX + 4 + (idx % vehicleColsPerRow) * vehicleColWidth;
      const vy = orarioTop + 14 + 4 + Math.floor(idx / vehicleColsPerRow) * rowH;
      drawCheckbox(vx, vy, v.selected, 6);
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(7);
      pdf.setTextColor(COLOR_TEXT);
      const label = v.name + (v.plate ? ` (${v.plate})` : '');
      pdf.text(label, vx + 9, vy + 5, { maxWidth: vehicleColWidth - 12 });
    });
  }

  y = orarioTop + orarioBlockH;

  // ── Firme ───────────────────────────────────────────────────────────────
  const firmaBoxH = 60;
  const firmaBlockH = 14 + 12 + firmaBoxH;
  ensureSpace(firmaBlockH);
  const firmaTop = y;
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(7);
  pdf.setTextColor(COLOR_TEXT);
  pdf.text(`FIRMA EGR (${compiledByLabel})`.toUpperCase(), opX + 4, firmaTop + 8);
  pdf.text('FIRMA CLIENTE'.toUpperCase() + (data.customerSignerName ? ` (${data.customerSignerName})` : ''), prX + 4, firmaTop + 8);

  const boxTop = firmaTop + 12;
  pdf.setDrawColor(COLOR_BORDER);
  pdf.setLineWidth(0.5);
  pdf.rect(opX, boxTop, halfWidth, firmaBoxH, 'S');
  pdf.rect(prX, boxTop, halfWidth, firmaBoxH, 'S');

  if (employeeSigImg) {
    const { w, h } = fitImage(employeeSigImg, halfWidth - 10, firmaBoxH - 10);
    pdf.addImage(employeeSigImg.pngDataUrl, 'PNG', opX + (halfWidth - w) / 2, boxTop + (firmaBoxH - h) / 2, w, h);
  } else {
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(7);
    pdf.setTextColor(COLOR_BORDER);
    pdf.text('Firma non disponibile', opX + halfWidth / 2, boxTop + firmaBoxH / 2, { align: 'center' });
  }

  if (customerSigImg) {
    const { w, h } = fitImage(customerSigImg, halfWidth - 10, firmaBoxH - 10);
    pdf.addImage(customerSigImg.pngDataUrl, 'PNG', prX + (halfWidth - w) / 2, boxTop + (firmaBoxH - h) / 2, w, h);
  } else {
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(7);
    pdf.setTextColor(COLOR_BORDER);
    pdf.text('Firma non disponibile', prX + halfWidth / 2, boxTop + firmaBoxH / 2, { align: 'center' });
  }

  y = boxTop + firmaBoxH + 6;

  // ── Valutazione servizio ────────────────────────────────────────────────
  sectionHeader('Valutazione servizio');
  y += 10;
  ensureSpace(10);
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(7.5);
  pdf.setTextColor(COLOR_TEXT);
  pdf.text('Valuti la nostra prestazione, ci aiuterà a migliorare i nostri servizi', MARGIN + 4, y);
  y += 10;
  ensureSpace(12);
  let ratingX = MARGIN + 4;
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(7.5);
  SERVICE_RATING_OPTIONS.forEach(opt => {
    drawCheckbox(ratingX, y - 6, data.serviceRating === opt.value, 6);
    pdf.setTextColor(COLOR_TEXT);
    pdf.text(opt.label.toUpperCase(), ratingX + 10, y);
    ratingX += 10 + pdf.getTextWidth(opt.label.toUpperCase()) + 14;
  });
  y += 14;

  // ── Footer ──────────────────────────────────────────────────────────────
  ensureSpace(34);
  pdf.setDrawColor(COLOR_BORDER);
  pdf.setLineWidth(0.5);
  pdf.line(MARGIN, y, MARGIN + CONTENT_WIDTH, y);
  y += 9;
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(6.5);
  pdf.setTextColor(COLOR_TEXT);
  pdf.text('EGR SRL', MARGIN, y);
  pdf.text('ASSISTENZA H24', MARGIN + CONTENT_WIDTH, y, { align: 'right' });
  y += 9;
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(COLOR_MUTED);
  pdf.text('Via Grecia, 8 – 70032 Bitonto (BA) — P.IVA 08762090721', MARGIN, y);
  pdf.text('080 2223343', MARGIN + CONTENT_WIDTH, y, { align: 'right' });
  y += 9;
  pdf.text('Mail: info@egrsrl.com — Pec: egrsrlbitonto@pec.it — Web: www.ecogrouprizzi.it', MARGIN, y);
  pdf.text('Cell. 393 8789920 / 389 0018570', MARGIN + CONTENT_WIDTH, y, { align: 'right' });

  return pdf.output('blob');
};
